import { Autocomplete, Box, Chip, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useField } from "@unform/core";
import React, { useEffect, useMemo, useState } from "react";
import { clientService } from "../../services/api/client/clientService";

type TPartnerOption = {
	id: string;
	label: string;
	tradeName?: string;
	email?: string;
	typeLabel?: string;
	status?: string;
};

interface IAutoCompletePartnerProps {
	isExternalLoading?: boolean;
	labelSelect?: string;
	namecampo?: string;
}

type TStatus = "active" | "inactive";

const normalizeStatus = (raw: unknown): TStatus => {
	const value = String(raw ?? "").toLowerCase();
	if (value === "inactive" || value === "0" || value.includes("inativ")) return "inactive";
	return "active";
};

const statusChipSx = (status: TStatus) =>
	status === "inactive"
		? ({ color: "#e40909", fontWeight: 500, background: "#fce4e4", border: "0.5px solid #f1f1f1" } as const)
		: ({ color: "#0f8a0d", fontWeight: 500, background: "#d9f8e5", border: "0.5px solid #f1f1f1" } as const);

export const AutoCompletePartner: React.FC<IAutoCompletePartnerProps> = ({
	isExternalLoading = false,
	labelSelect = "Parceiro",
	namecampo = "partnerId",
}) => {
	const { fieldName, registerField, defaultValue, error, clearError } = useField(namecampo);
	const [selectedId, setSelectedId] = useState<string | undefined>(defaultValue);
	const [options, setOptions] = useState<TPartnerOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		registerField({
			name: fieldName,
			getValue: () => selectedId,
			setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
		});
	}, [registerField, fieldName, selectedId]);

	useEffect(() => {
		setIsLoading(true);
		clientService
			.getTypePartners()
			.then((result) => {
				if (result instanceof Error) {
					setOptions([]);
					return;
				}
				setOptions(
					result.data.map((partner) => ({
						id: partner.id,
						label: partner.name,
						tradeName: partner.trade_name,
						email: partner.email,
						typeLabel: (partner as any).clientType?.description,
						status: (partner as any).status,
					})),
				);
			})
			.catch(() => setOptions([]))
			.finally(() => setIsLoading(false));
	}, []);

	const selectedOption = useMemo(() => options.find((option) => option.id === selectedId) ?? null, [options, selectedId]);

	return (
		<Autocomplete
			openText="Abrir"
			closeText="Fechar"
			noOptionsText="Nenhum parceiro encontrado"
			loadingText="Carregando..."
			disablePortal
			value={selectedOption}
			options={options}
			loading={isLoading}
			size="small"
			disabled={isExternalLoading}
			getOptionLabel={(option) => option.label}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			onChange={(_, newValue) => {
				setSelectedId(newValue?.id);
				clearError();
			}}
			renderOption={(props, option) => {
				const status = normalizeStatus(option.status);
				return (
					<Box
						component="li"
						{...props}
						key={option.id}
						sx={{ display: "flex", flexDirection: "column", alignItems: "stretch !important", gap: 0.4, py: "8px !important" }}
					>
						<Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" gap={1.5}>
							<Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
								{option.label}
							</Typography>
							<Stack direction="row" spacing={1} flexShrink={0}>
								{option.typeLabel && <Chip size="small" variant="outlined" label={option.typeLabel} sx={{ fontWeight: 500 }} />}
								<Chip size="small" label={status === "inactive" ? "Inativo" : "Ativo"} sx={statusChipSx(status)} />
							</Stack>
						</Stack>
						{option.tradeName && (
							<Typography variant="caption" color="text.secondary" noWrap>
								{option.tradeName}
							</Typography>
						)}
						{option.email && (
							<Typography variant="caption" color="text.secondary" noWrap>
								{option.email}
							</Typography>
						)}
					</Box>
				);
			}}
			popupIcon={isExternalLoading || isLoading ? <CircularProgress size={20} /> : undefined}
			renderInput={(params) => <TextField {...params} label={labelSelect} error={!!error} helperText={error} />}
		/>
	);
};
